<script lang="ts">
  import {TimestampText} from '@/store/recap';
  import {createEventDispatcher, onDestroy, tick} from 'svelte';

  const dispatch = createEventDispatcher();

  let highlightContainer: HTMLDivElement;
  let searchWord = '';

  export let transcriptions: TimestampText[] = [];

  let textHighlightReference: {startIndex: number; endIndex: number}[] = [];

  function selectHighlight(index: number) {
    if (transcriptions[index].isHighlighted) {
      const reference =
        textHighlightReference.find(({startIndex, endIndex}) => {
          return (isIndexStart && startIndex === index) || (!isIndexStart && endIndex === index);
        }) || null;
      if (!reference) return console.error("text is highlighted but there's no reference");
      if (reference.startIndex !== reference.endIndex) return;

      transcriptions[index].isHighlighted = false;
      textHighlightReference = textHighlightReference.filter(x => x !== reference);
      triggerRerender();

      return;
    }

    transcriptions[index].isHighlighted = true;
    transcriptions = transcriptions;
    textHighlightReference.push({startIndex: index, endIndex: index});
  }

  function triggerRerender() {
    transcriptions = [...transcriptions];
  }

  let currentReference: {startIndex: number; endIndex: number} | null = null;
  let isIndexStart = false;

  function selectReference(index: number, isStart: boolean) {
    isIndexStart = isStart;
    currentReference =
      textHighlightReference.find(({startIndex, endIndex}) => {
        return (isIndexStart && startIndex === index) || (!isIndexStart && endIndex === index);
      }) || null;

    // console.log("SELECT REFERENCE", { isIndexStart, currentReference });
  }

  function onMouseMove(e: MouseEvent) {
    if (!currentReference) return;

    const mouse = {x: e.clientX, y: e.clientY};
    const rect = highlightContainer.getBoundingClientRect();

    // console.log({ mouse, rect });
    if (!(mouse.y >= rect.top && mouse.y <= rect.bottom && mouse.x >= rect.left && mouse.x <= rect.right)) return;

    const textElements = Array.from(highlightContainer.querySelectorAll('span'));
    textElements.forEach((textElement, i) => {
      if (!currentReference) return;

      const rect = textElement.getBoundingClientRect();
      if (!(mouse.y >= rect.top && mouse.y <= rect.bottom && mouse.x >= rect.left && mouse.x <= rect.right)) return;

      // console.log({ text : transcriptions[i].text });

      if (isIndexStart) {
        currentReference.startIndex = i;
      } else {
        currentReference.endIndex = i;
      }

      if (currentReference.endIndex < currentReference.startIndex) {
        [currentReference.startIndex, currentReference.endIndex] = [
          currentReference.startIndex,
          currentReference.endIndex
        ];
        isIndexStart = false;
      }
    });

    // console.log("REFERENCE", currentReference);

    for (let i = 0; i < textHighlightReference.length; i++) {
      const textReference = textHighlightReference[i];

      if (
        (currentReference.startIndex >= textReference.startIndex &&
          currentReference.startIndex <= textReference.endIndex) ||
        (currentReference.endIndex >= textReference.startIndex && currentReference.endIndex <= textReference.endIndex)
      ) {
        if (textReference === currentReference) continue;

        if (textReference.startIndex <= currentReference.startIndex) {
          currentReference.startIndex = textReference.startIndex;
        }

        if (textReference.endIndex >= currentReference.endIndex) {
          currentReference.endIndex = textReference.endIndex;
        }

        textReference.startIndex = -1;
        textReference.endIndex = -1;

        deselectReference();
      }
    }

    textHighlightReference = textHighlightReference.filter(({startIndex, endIndex}) => {
      return startIndex > -1 && endIndex > -1;
    });

    // console.log("ALL REFERENCE", textHighlightReference);

    const timestamps = textHighlightReference
      .map(({startIndex, endIndex}) => {
        return {
          startDuration: transcriptions[startIndex].timeStart,
          endDuration: transcriptions[endIndex].timeEnd,
          startIndex,
          endIndex
        };
      })
      .sort((a, b) => {
        return a.startDuration - b.startDuration;
      });

    for (let j = 0; j < transcriptions.length; j++) {
      const text = transcriptions[j];
      text.isHighlighted = false;
      for (let i = 0; i < textHighlightReference.length; i++) {
        const textReference = textHighlightReference[i];
        if (j >= textReference.startIndex && j <= textReference.endIndex) {
          text.isHighlighted = true;
        }
      }
    }

    dispatch('timestamp-change', {timestamps});
    triggerRerender();
  }

  function deselectReference() {
    currentReference = null;
    isIndexStart = false;

    // console.log("END REFERENCE", { isIndexStart, currentReference });
  }

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', deselectReference);

  onDestroy(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', deselectReference);
  });

  $: {
    if (searchWord.length > 0) search();
  }

  async function search() {
    console.log('Searching word:', searchWord);
    wordSearchIndex = -1;
    for (let i = 0; i < transcriptions.length; i++) {
      transcriptions[i].isSearchWord = false;
      if (
        transcriptions[i].text.toLowerCase().replace(/[^a-zA-Z ]/g, '') ===
        searchWord.replace(/[^a-zA-Z ]/g, '').toLowerCase()
      ) {
        transcriptions[i].isSearchWord = true;
      }
    }
    transcriptions = transcriptions;
    await tick();
    nextWord();
  }

  let wordSearchIndex = -1;

  function previousWord() {
    const searchWordElements = Array.from(highlightContainer.querySelectorAll('div .search-word'));
    console.log('previous words', {searchWordElements, wordSearchIndex});

    wordSearchIndex--;
    if (wordSearchIndex < 0) wordSearchIndex = searchWordElements.length - 1;

    if (!searchWordElements[wordSearchIndex]) return;
    searchWordElements[wordSearchIndex].scrollIntoView();
  }

  function nextWord() {
    const searchWordElements = Array.from(highlightContainer.querySelectorAll('div .search-word'));
    console.log('next words', {searchWordElements, wordSearchIndex});

    wordSearchIndex++;
    if (wordSearchIndex >= searchWordElements.length) wordSearchIndex = 0;

    if (!searchWordElements[wordSearchIndex]) return;
    searchWordElements[wordSearchIndex].scrollIntoView();
  }

  let transcriptionLength = 0;
  $: transcriptionLength = transcriptions.length - 1;

  let spacingIndex = new Map();
  let textLength = 0;
  const defaultMinTextLength = 400;
  let minTextLength = defaultMinTextLength;

  $: if (transcriptions.length >= 0) {
    minTextLength =
      transcriptions.length < defaultMinTextLength
        ? Math.round(transcriptions.reduce((count, word) => count + word.text.length, 0) / 2)
        : defaultMinTextLength;

    spacingIndex.clear();
    for (let i = 0; i < transcriptions.length; i++) {
      textLength += transcriptions[i].text.length;
      if ((textLength >= minTextLength && transcriptions[i].text.includes('.')) || i === transcriptions.length - 1) {
        spacingIndex.set(i, true);
        textLength = 0;
      }
    }
  }

  const eventDelegationType = {
    selectHighlight: 'select-highlight',
    selectReference: 'select-refenrece'
  };

  function eventDelegation(node: HTMLDivElement) {
    const click = (event: Event) => {
      const targetElement = event.target;
      if (!(targetElement instanceof HTMLSpanElement)) return;

      const index = parseInt(targetElement.dataset.index || '');
      if (isNaN(index) || index < 0) return console.error('eventDelegation click index is not a number', {index});

      selectHighlight(index);
    };

    const mousedown = (event: MouseEvent) => {
      const targetElement = event.target;
      if (!(targetElement instanceof HTMLButtonElement)) return;

      const type = targetElement.dataset.type;
      if (type !== eventDelegationType.selectReference) return;

      const index = parseInt(targetElement.dataset.index || '');
      if (isNaN(index) || index < 0) return console.error('eventDelegation mousedown index is not a number', {index});

      const isStart = targetElement.dataset.start === 'true';
      selectReference(index, isStart);
    };

    node.addEventListener('click', click);
    node.addEventListener('mousedown', mousedown);

    return {
      destroy: () => {
        node.removeEventListener('click', click);
        node.removeEventListener('mousedown', mousedown);
      }
    };
  }
</script>

<div class="h-full w-full overflow-hidden p-4" style="max-width: 540px;">
  <div>
    <h1 class="text-xl font-bold">Highlight Moments</h1>
  </div>
  <div class="flex">
    <div class="relative flex w-full gap-2 pt-3">
      <div class="absolute flex items-center py-3 pl-3 pr-2">
        <i class="fa-solid fa-magnifying-glass text-md text-zinc-300" aria-hidden="true"></i>
      </div>
      <input bind:value={searchWord} class="w-full rounded-lg border border-zinc-300 py-2 pl-10 pr-2" />
      <button on:click={previousWord} class="rounded-lg border border-zinc-300 p-2 px-3">
        <i class="fa-solid fa-arrow-up text-zinc-400" aria-hidden="true"></i>
      </button>
      <button on:click={nextWord} class="rounded-lg border border-zinc-300 p-2 px-3">
        <i class="fa-solid fa-arrow-down text-zinc-400" aria-hidden="true"></i>
      </button>
    </div>
  </div>
  <div class="relative h-full w-full overflow-auto" style="max-height: calc(100% - 4.25rem);">
    <div use:eventDelegation bind:this={highlightContainer} class="noselect flex w-full flex-wrap pt-2">
      {#each transcriptions as text, i}
        <div
          class="{text.isSearchWord
            ? 'search-word bg-emerald-500/50'
            : text.isFollowingText
              ? 'bg-amber-500/50'
              : text.isHighlighted
                ? 'bg-rose-500/50'
                : ''} flex grow {text.isHighlighted &&
          ((transcriptions[i - 1] && !transcriptions[i - 1].isHighlighted) || i === 0)
            ? 'rounded-bl rounded-tl'
            : ''}
                {text.isHighlighted &&
          ((transcriptions[i + 1] && !transcriptions[i + 1].isHighlighted) || i === transcriptionLength)
            ? 'rounded-br rounded-tr'
            : ''}"
        >
          {#if text.isHighlighted && ((transcriptions[i - 1] && !transcriptions[i - 1].isHighlighted) || i === 0)}
            <button
              data-type={eventDelegationType.selectReference}
              data-index={i}
              data-start="true"
              class="mr-auto rounded-bl rounded-tl bg-zinc-500">&nbsp;&nbsp;</button
            >
          {:else}
            &nbsp;&nbsp;
          {/if}
          <span data-index={i}>{text.text}</span>
          {#if text.isHighlighted && ((transcriptions[i + 1] && !transcriptions[i + 1].isHighlighted) || i === transcriptionLength)}
            <button
              data-type={eventDelegationType.selectReference}
              data-index={i}
              data-start="false"
              class="ml-auto rounded-br rounded-tr bg-zinc-500">&nbsp;&nbsp;</button
            >
          {:else}
            &nbsp;&nbsp;
          {/if}
        </div>
        {#if spacingIndex.get(i)}
          <div style="flex-grow: 100;"></div>
          <div style="flex-grow: 1; width: 100%; flex-grow: 1; height: 32px;"></div>
        {/if}
      {/each}
    </div>
  </div>
</div>

<style>
  .noselect {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                                        supported by Chrome, Edge, Opera and Firefox */
  }
</style>
